import { REGIONS_BY_COUNTRY } from "~/lib/country-regions"

/**
 * #1392 — event `location` strings are free text, and the only established
 * convention this app has for them is Australian events writing the state as
 * a 2-3 letter abbreviation ("Bondi Beach, NSW"). The other countries in
 * country-regions.ts have no such convention, so their regions are
 * recognised by full name instead ("Auckland", "California").
 */
export const AU_STATE_ABBR: Readonly<Record<string, string>> = {
  NSW: "New South Wales",
  VIC: "Victoria",
  QLD: "Queensland",
  SA: "South Australia",
  WA: "Western Australia",
  TAS: "Tasmania",
  NT: "Northern Territory",
  ACT: "Australian Capital Territory",
}

const AU_ABBR_PATTERN = new RegExp(`\\b(${Object.keys(AU_STATE_ABBR).join("|")})\\b`, "i")

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

// Every offered country's region by full name, Australia included — "New
// South Wales" is a real, common way to write that AU state in full, and it
// is also, inconveniently, a superstring of the UK's "Wales". Keeping AU out
// of this list (the original design) left "New South Wales" to fall through
// to the UK match — a worse bug than any abbreviation collision, since it
// misreads an unambiguous, fully-spelled-out Australian address. Longest
// first so a region whose name contains another region's name as a
// substring (today: "New South Wales" ⊃ "Wales"; "West Virginia" ⊃
// "Virginia") never gets shadowed by the shorter match.
const ALL_REGION_NAMES = Object.values(REGIONS_BY_COUNTRY)
  .flat()
  .sort((a, b) => b.length - a.length)

const FULL_REGION_PATTERN = ALL_REGION_NAMES.length
  ? new RegExp(`\\b(${ALL_REGION_NAMES.map(escapeRegExp).join("|")})\\b`, "i")
  : null

/**
 * The full region name found in `location`, or null. Checks every offered
 * country's region by full name first (unambiguous — no two supported
 * countries share a full region name, and the longest-first sort stops a
 * full name that contains another region's name as a substring, such as
 * "New South Wales" ⊃ "Wales", from matching the shorter one), then the
 * Australian abbreviation convention (existing behaviour, unchanged) as a
 * fallback for AU addresses that abbreviate instead of spelling out.
 *
 * Known, accepted limitation (reviewer gate, PR #1463): two of the eight AU
 * abbreviations collide with real-world non-AU usage this function cannot
 * see, because event `location` carries no structured country — "WA" also
 * abbreviates US Washington, and "NT" also abbreviates Canada's Northwest
 * Territories. A US/Canadian event written with its 2-letter postal
 * abbreviation ("Seattle, WA") is read as the Australian state. Australia
 * is the only country with real location data today and the only one with
 * an abbreviation convention at all (see the module comment above), so it
 * wins the tie-break; a real fix needs a structured country field on the
 * event, not a smarter regex. See the "collisions" test below.
 */
export function detectRegionInLocation(location: string | null | undefined): string | null {
  if (!location?.trim()) return null
  const fullNameMatch = FULL_REGION_PATTERN ? location.match(FULL_REGION_PATTERN) : null
  if (fullNameMatch?.[1]) {
    // Case-insensitive matching means the match can differ in case from the
    // canonical name (e.g. "western australia"); look it up to normalise.
    const canonical = ALL_REGION_NAMES.find(
      (r) => r.toLowerCase() === fullNameMatch[1]!.toLowerCase(),
    )
    return canonical ?? fullNameMatch[1]
  }
  const auMatch = location.match(AU_ABBR_PATTERN)
  return auMatch?.[1] ? AU_STATE_ABBR[auMatch[1].toUpperCase()] ?? null : null
}
